import { FC, useMemo, useCallback, useState, forwardRef, useContext } from 'react';
import { AppBar, backdropClasses, Box, Button, Chip, colors, Dialog, Divider, IconButton, List, Paper, Slide, styled, Toolbar, Typography, useTheme } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays, faCodeBranch, faEarthOceania, faInputText, faLinkHorizontal, faListOl, faObjectGroup, faSlash, faSquareList, faTableList } from "@fortawesome/duotone-light-svg-icons";
import { Checkbox, IHasChildren, InputOnlyField, IValidationError, Text } from "@ngt/forms";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FieldType } from "@/api/dtos";
import { ConfigurationType, IConfigurationItem, ISourceDataConfigurationItem } from "@/utilities/forms/ConfigurationFormProcessor";
import ConfigurationFormEditContext, { IConfigurationFormEditContext } from "@/contexts/forms/ConfigurationFormEditContext";
import { Condition, ConditionMode, useFormActions, useScopedField } from '@ngt/forms-core';
import { TransitionProps } from '@mui/material/transitions';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import ConfigurationFormEditInfo from './ConfigurationFormEditInfo';
import { grey } from '@mui/material/colors';
import AppSettings from '@/AppSettings';

export const ConfigurationIconBox = styled(Box)(({theme}) => ({
    fontSize: '1.5rem',
    marginRight: theme.spacing(2)
}));

const TitleBox = styled(Box)(({theme}) => ({
    lineHeight: theme.spacing(3.75),
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2),
    marginTop: theme.spacing(-2),
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(2),
    
    paddingRight: theme.spacing(2),
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    textTransform: 'uppercase',
    fontWeight: 500,
    color: theme.palette.primary.main
}));

const DividerBox = styled(Box)(({theme}) => ({
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2)
}));

const InfoBox = styled(Paper)(({theme}) => ({
    padding: theme.spacing(0, 2),
    
    margin: theme.spacing(0, 2, 2, 2)
}));

export const ConfigurationBox = styled(Paper)(({theme}) => ({
    padding: theme.spacing(2, 2),
    margin: theme.spacing(0, 2, 2, 2),
    color: theme.palette.mode === 'dark' ? 
        AppSettings.palette.backgroundAltDark :
        'white',
}));

export const ConfigurationTitle = styled(Typography)(({theme}) => ({
    margin: theme.spacing(2, 2, 2, 2)
}));

export const ConfigurationFlexBox = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
}));

export const ConfigurationInfoChip = styled(Chip)(({theme}) => ({
    marginLeft: 'auto',
}));

const LinkButton = styled(IconButton)(({auto, theme}: any) => ({
    marginLeft: auto ? 'auto' : theme.spacing(2)
}));

const IconStack = styled(Box)(({theme}) => ({
    position: 'relative'
}));

const IconStacked = styled(Box)(({theme}) => ({
    position: 'absolute'
}));

const IconBase = styled(Box)(({linked, theme}: any) => ({
    opacity: linked ? undefined : 0.5
}));

const Buttons = styled(Box)(({theme}) => ({
    textAlign: 'right',
    paddingTop: theme.spacing(2)
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ?
    '#121212' :
    'white',

    color: theme.palette.mode === 'dark' ?
    theme.palette.grey[300] :
    theme.palette.primary.main,
}));

const onConditionNotMet = () => {};

export interface IConfigurationFormEditEventProps extends IHasChildren {
    context: IConfigurationFormEditContext
    type: string;
    typeIcon: IconProp;
}

const ConfigurationFormEditPanel: FC<IConfigurationFormEditEventProps> = ({
    type,
    typeIcon,
    context,
    children
}) => {
    const actions = useFormActions();
    const editContextObserver = useContext(ConfigurationFormEditContext);
    
    const theme = useTheme();

    const { state: { value: linked } } = useScopedField<boolean, any, IValidationError>(`${context.configurationLocalPropertyPath}.linked`, { value: true });
    const { state: { value: globalLinked } } = useScopedField<boolean, any, IValidationError>(`${context.configurationGlobalPropertyPath}.linked`, { value: true });

    const usingTrialConfiguration = context.usingTrialConfiguration;
    const usingGlobalConfiguration = context.usingGlobalConfiguration;

    const handleClose = () => {
        editContextObserver.set(undefined);
    };

    const fieldTypeLabel =  useMemo(() => {
        if (context.item.type === ConfigurationType.Field || context.item.type === ConfigurationType.Value){
            switch (context.item.formFieldMetadata?.fieldType) {
                case FieldType.Checkbox:
                    return 'Checkbox';
                case FieldType.Ctcae:
                    return 'CTCAE';
                case FieldType.Date:
                    return 'Date';
                case FieldType.File:
                    return 'File';
                case FieldType.MultiSelect:
                    return 'MultiSelect';
                case FieldType.MultilineText:
                    return 'MultilineText';
                case FieldType.Numeric:
                    return 'Numeric';
                case FieldType.Radio:
                    return 'Radio';
                case FieldType.Select:
                    return 'Select';
                case FieldType.Text:
                    return 'Text';
                default:
                    return 'Unknown';
            }
        }

        return undefined;
    }, [context])

    const conditionFn = useCallback(() => {
        return !context.usingTrialConfiguration;
    }, [context.usingTrialConfiguration]);

    const link = () => {
        const newLinked = context.link();

        let newPropertyPath = context.configurationLocalPropertyPath;
        let newUsingGlobalConfiguration = false;
        let newUsingTrialConfiguration = false;

        if (newLinked) {
            if (context.item.isGlobalConfiguration || globalLinked || context.item.type === ConfigurationType.Event) {
                newPropertyPath = context.configurationTrialPropertyPath;
                newUsingGlobalConfiguration = false;
                newUsingTrialConfiguration = true;
            }
            else {
                newPropertyPath = context.configurationGlobalPropertyPath;
                newUsingGlobalConfiguration = true;
                newUsingTrialConfiguration = false;
            }
        }
        else {
            if (context.item.isGlobalConfiguration) {
                newPropertyPath = context.configurationGlobalPropertyPath;
                newUsingGlobalConfiguration = true;
                newUsingTrialConfiguration = false;
            }
            else {
                newPropertyPath = context.configurationLocalPropertyPath;
                newUsingGlobalConfiguration = false;
                newUsingTrialConfiguration = false;
            }
        }

        editContextObserver.set({
            ...context,
            configurationPropertyPath: newPropertyPath,
            usingGlobalConfiguration: newUsingGlobalConfiguration,
            usingTrialConfiguration: newUsingTrialConfiguration
        })
    }

    return (
        <>
        <AppBar position='sticky'>
                <StyledToolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={handleClose}
                        aria-label="close"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </IconButton>
                    <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                        { 
                            usingTrialConfiguration ?
                                `View ${type} Configuration` :
                                usingGlobalConfiguration ?
                                `Global ${type} Configuration` :
                                `${type} Configuration` 
                        }
                    </Typography>
                    {
                        !context.item.isTrialConfiguration && (
                            <LinkButton
                                // @ts-ignore: used for styling
                                auto="1"
                                color="info"
                                onClick={link}
                            >
                                <IconStack>
                                    {/* {
                                        isLinking && (
                                            
                                            <IconStacked>
                                                <CircularProgress sx={{ml: '1px'}} size="1em" />
                                            </IconStacked>
                                        )
                                    } */}
                                    {
                                        !linked && (
                                            <IconStacked>
                                                <FontAwesomeIcon icon={ faSlash} fixedWidth />
                                            </IconStacked>
                                        )
                                    }
                                    <IconBase
                                        //@ts-ignore
                                        linked={linked ? "1" : undefined}
                                    >
                                        <FontAwesomeIcon 
                                            icon={
                                                !context.usingTrialConfiguration && !globalLinked && context.item.type !== ConfigurationType.Event && !context.item?.isGlobalConfiguration ?
                                                    faLinkHorizontal :
                                                    faEarthOceania
                                            } 
                                            fixedWidth 
                                        />
                                    </IconBase>
                                </IconStack>
                            </LinkButton>
                        )
                    }
                </StyledToolbar>
            </AppBar>
            <ConfigurationTitle variant="h2" color={theme.palette.secondary.main}>
                Details
            </ConfigurationTitle>
            <InfoBox>
                <ConfigurationFormEditInfo 
                    {...context.item}
                    formFieldTypeLabel={fieldTypeLabel}
                    isGlobal={context.usingGlobalConfiguration && !context.item.isTrialConfiguration}
                    isTrial={context.usingTrialConfiguration || !!context.item.isTrialConfiguration}
                    dividers
                />
            </InfoBox>
            <Condition 
                mode={ConditionMode.Enable}
                subscription={{}}
                condition={conditionFn}
                onConditionNotMet={onConditionNotMet}
            >
                {children}
            </Condition>
        </>
    )
}

export default ConfigurationFormEditPanel;